import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux';
import * as hookActions from 'checkout/actions/hooks';
import { getSeriesConfig, inLastStep } from '../../selectors/ui';
import { toDecimals } from '../../helpers/currency';
import { getOrderTotal } from '../../selectors/cart';
import { getSeriesOption } from '../../views/series/selectors/eventSeries';
import { TICKETS_DISPLAY_LIST } from '../../reducers/ui';
import { EcommerceShared } from './EcommerceShared';
import { getMainEvent, getSelectedEvent, isSeries } from '@b/checkout/selectors/event';

const nullFallback = 'Not Set';

export class GoogleAnalytics4 extends Component {
    reportEvent = (type, args) => {
      let destination = this.props.config.measurement_id;
      if (!destination) {
          destination = this.props.config.floodlight_id;
      }
      const defaultArgs = this.getDefaultArgs();
      this.props.reportEvent(destination)('event', type, {
          ...args,
          ...defaultArgs,
          // route this event to this measurement ID - in case of other GA4 integrations on the page
          send_to: destination
      });
    }

    getDefaultArgs = () => {
      return {
        tickets_display_type: this.getFinalTicketsDisplayType(),
      }
    }

    report = (dataLayer) => {
      if (!dataLayer) {
        return;
      }

      this.reportEvent(dataLayer.event, dataLayer.ecommerce);
    }

    constructor(props) {
        super(props);

        this.state = {
          lastInsuranceEvent: null,
          hasSelectedEvent: false,
        }

        if (props.config?.measurement_id) {
          this.props.addReservationCompleteHook(this.onReservationComplete);
          this.props.addTicketsAddedToCartHook(this.onTicketsAdded);
          this.props.addProductsAddedToCartHook(this.onProductsAdded);
          this.props.addBundlesAddedToCartHook(this.onBundlesAdded);
          this.props.addTicketsRemovedFromCartHook(this.onTicketsRemoved);
          this.props.addProductsRemovedFromCartHook(this.onProductsRemoved);
          this.props.addBundlesRemovedFromCartHook(this.onBundlesRemoved);
          this.props.addStateTransitionHook(this.onStateTransition);
          this.props.addReservationClearHook(this.onReservationClear);
          this.props.addReservationExpiresInHook(this.onReservationExpiresIn);
          this.props.addCartTimerExtendedHook(this.onCartTimerExtended);
          this.props.addInsuranceOptionChangedHook(this.onInsuranceOptionChanged);
          this.props.addCalendarDateSelectedHook(this.onCalendarDateSelected);
          this.props.addCalendarTimeSelectedHook(this.onCalendarTimeSelected);
          this.props.addCalendarDateChangedHook(this.onCalendarDateChanged);
          this.props.addCalendarViewHook(this.onCalendarView);
          this.props.addTicketIncrementHook(this.onItemQuantityChange('ticket', 'increment'));
          this.props.addTicketDecrementHook(this.onItemQuantityChange('ticket', 'decrement'));
          this.props.addTicketChangeQuantityHook(this.onItemQuantityChange('ticket'));
          this.props.addProductIncrementHook(this.onItemQuantityChange('product', 'increment'));
          this.props.addProductDecrementHook(this.onItemQuantityChange('product', 'decrement'));
          this.props.addProductChangeQuantityHook(this.onItemQuantityChange('product'));
          this.props.addBundleIncrementHook(this.onItemQuantityChange('bundle', 'increment'));
          this.props.addBundleDecrementHook(this.onItemQuantityChange('bundle', 'decrement'));
          this.props.addBundleChangeQuantityHook(this.onItemQuantityChange('bundle'));
          this.props.addCheckoutOverlayChangedHook(this.onCheckoutOverlayChanged);
          this.props.addMembershipViewHook(this.onMembershipView);
          this.props.addMembershipSuccessSignInHook(this.onMembershipSuccessSignIn);
          this.props.addMembershipSkipSignInHook(this.onMembershipSkipSignIn);
          this.props.addMembershipGoToSignInHook(this.onMembershipGoToSignIn);
          this.props.addMembershipSignOutHook(this.onMembershipSignOut);
        }
    }

    componentDidMount() {
      this.report(EcommerceShared.getEventInfoLayer(this.props));
    }

    render() {
        return null;
    }

    getFinalTicketsDisplayType = () => {
      if (this.props.isSeries) {
        return this.props.calendarOnListing ? 'calendar' : 'button';
      }
  
      return this.props?.ticketsDisplayType === TICKETS_DISPLAY_LIST ? 'list' : 'button';
    }

    onReservationComplete = () => {
        const reservationSessionKey = `GoogleAnalytics4_${this.props.config.measurement_id}_onReservationComplete_${this.props.confirmationNumber}`;
        const sent = sessionStorage.getItem(reservationSessionKey);
        if (!sent) {
            // add_payment_info event
            if (Array.isArray(this.props.payments) && this.props.payments.length) {
                const payment = this.props.payments[0];
                if (payment?.payment_method) {
                    this.reportWholeReservationEvent('add_payment_info', {
                        payment_type: payment.payment_method,
                    });
                }
            }
      
            this.reportEvent('event_registration', {
              event_reg_name: this.props.mainEvent?.name || nullFallback,
              event_reg_series: this.props.listingSlug,
              event_platform: this.props.isSeries ? this.props.mainEvent?.event_id : nullFallback,
              event_reg_id: this.props.selectedEvent?.event_id || nullFallback,
              venue_location: this.props.selectedVenueId || nullFallback,
              location_id: this.props.selectedVenueId || nullFallback, 
              location_detail: this.props.selectedVenueId || nullFallback,
              quantity: this.props.reservation?.items_count || nullFallback
            });

            // purchase event
            this.reportWholeReservationEvent('purchase', {
              transaction_id: this.props.confirmationNumber,
              revenue: toDecimals({amount: this.props.orderTotal, currency: this.props.currency}),
              insurance: this.props.hasInsurance ? "On" : "Off",
            });

            sessionStorage.setItem(reservationSessionKey, '1');
        }
    }

    onTicketsAdded = tickets => {
        this.reportUpdateCartItemsEvent('add_to_cart', 'ticket', tickets);
    }

    onProductsAdded = products => {
        this.reportUpdateCartItemsEvent('add_to_cart', 'product', products);
    }

    onBundlesAdded = bundles => {
        this.reportUpdateCartItemsEvent('add_to_cart', 'bundle', bundles);
    }

    onTicketsRemoved = tickets => {
        this.reportUpdateCartItemsEvent('remove_from_cart', 'ticket', tickets);
    }

    onProductsRemoved = products => {
        this.reportUpdateCartItemsEvent('remove_from_cart', 'product', products);
    }

    onBundlesRemoved = bundles => {
        this.reportUpdateCartItemsEvent('remove_from_cart', 'bundle', bundles);
    }

    reportUpdateCartItemsEvent = (eventType, itemType, items) => {
        this.reportEvent(eventType, {
            currency: this.props.currency,
            value: this.getTotalCostOfItems(items),
            items: items.map(item => this.formatItem(item, itemType)),
            quantity: items.length
        });
    }

    reportWholeReservationEvent = (eventType, extraFields = {}) => {
        this.reportEvent(eventType, {
            currency: this.props.currency,
            value: toDecimals({ amount: this.props.orderTotal, currency: this.props.currency }),
            items: this.formatAllReservationItems(),
            ...extraFields,
        });
    }

    formatAllReservationItems = () => {
        const items = [];
        this.props.tickets.forEach(ticket => {
            items.push(this.formatItem(ticket, 'ticket'));
        });
        this.props.productSales.forEach(product => {
            items.push(this.formatItem(product, 'product'));
        });
        this.props.bundleSales.forEach(bundle => {
            items.push(this.formatItem(bundle, 'bundle'));
        });
        return items;
    }

    onStateTransition = page => {
        if (page === 'cart') {
            this.reportWholeReservationEvent('view_cart');
        }

        // checkout step seen action
        if (typeof page === 'string' && page.substring(0, 9) === 'checkout-' && this.props.inLastStep) {
            this.reportWholeReservationEvent('begin_checkout');
        }
    }

    onReservationExpiresIn = (expiresIn) => {
        if (expiresIn === 60) {
            this.reportEvent('expiresIn', { expiresIn });
        }
    }

    onReservationClear = (_, reason) => {
      if (reason === 'expired') {
        this.reportEvent('cartExpired');
      }

      if (reason === 'clearCart') {
        this.reportEvent('clearCart');
      }
    }

    onCartTimerExtended = () => {
      this.reportWholeReservationEvent('cartTimerExtended');
    }

    onInsuranceOptionChanged = ({ event }) => {
      const eventName = event === 'accepted' ? 'insuranceAccepted' : 'insuranceDeclined';

      if (this.state.lastInsuranceEvent === event) {
        return;
      }

      if (this.state.lastInsuranceEvent) {
        this.reportEvent('insuranceChanged', {
          from: this.state.lastInsuranceEvent,
          to: event
        })
      } else {
        this.reportEvent(eventName);
      }

      this.setState({
        lastInsuranceEvent: event
      });
    }

    getMembershipEventData = () => {
        return {
            event_id: this.props.selectedEvent?.event_id || '',
            event_name: this.props.selectedEvent?.name || '',
            listing_slug: this.props.listingSlug || '',
            source_url: window.top.location.href,
        };
    }

    onMembershipView = () => {
        this.reportEvent('memberPageViewGate', this.getMembershipEventData());
    }

    onMembershipSuccessSignIn = () => {
        this.reportEvent('memberSignInGate', this.getMembershipEventData());
    }

    onMembershipSkipSignIn = (value) => {
        if (!this.props.skipMembership && value) {
            this.reportEvent('membershipSkipSignIn', this.getMembershipEventData());
        }
    }

    onMembershipGoToSignIn = () => {
        this.reportEvent('memberSignInEvent', this.getMembershipEventData());
    }

    onMembershipSignOut = () => {
        this.reportEvent('memberSignOutEvent', this.getMembershipEventData());
    }

    formatItem = (item, type) => {

        const itemIdFieldMap = {
            ticket: 'price_level_id',
            product: 'product_id',
            bundle: 'bundle_id',
        };

        const itemNameFieldMap = {
            ticket: 'price_level_name',
            product: 'product_name',
            bundle: 'name',
        };

        return {
            item_id: item?.[itemIdFieldMap[type]] + '',
            item_name: item?.[itemNameFieldMap[type]] + '',
            item_category: type,
            price: toDecimals({ amount: +item?.cost?.amount + +item?.buyer_fee?.amount, currency: this.props.currency }),
            quantity: 1,
        };
    }

    getTotalCostOfItems = items => {
        let cost = 0;
        if (Array.isArray(items)) {
            items.forEach(item => {
                cost += +(item?.cost?.amount ?? 0);
                cost += +(item?.buyer_fee?.amount ?? 0);
            });
        }
        return toDecimals({ amount: cost, currency: this.props.currency });
    }

    onCalendarDateSelected = (date) => {
      this.report(EcommerceShared.getCalendarDateSelectedLayer(date, this.props));
    }

    onCalendarTimeSelected = ({ event }) => {
      if (!this.props.overlayVisible) {
        return;
      }
  
      if (!event) {
        if (this.state.hasSelectedEvent) {
          this.setState({ hasSelectedEvent: false });

          this.report(EcommerceShared.getReportDateChangedLayer(this.props));
          return;
        }
        return;
      }
  
      this.setState({ hasSelectedEvent: true });

      this.report(EcommerceShared.getReportCalendarTimeSelectedLayer(event, this.props));
    }

    onCalendarDateChanged = () => {
      if (!this.props.overlayVisible) {
        return;
      }

      this.report(EcommerceShared.getReportDateChangedLayer(this.props));
    }

    onCalendarView = (data) => {
      this.report(EcommerceShared.getCalendarViewLayer(data, this.props));
    }

    onItemQuantityChange = (type, action) => (id, value) => {
      this.report(EcommerceShared.getItemQuantityChangeLayer(type, action, id, value, this.props));
    }

    onCheckoutOverlayChanged = (overlayVisible) => {
      if (!overlayVisible) {
        this.report(EcommerceShared.getEventInfoLayer(this.props));
      }
    }
}

const mapStateToProps = state => {
    const mainEvent = getMainEvent(state)?.event;
    const selectedEvent = getSelectedEvent(state)?.event;
    const selectedVenue = getSelectedEvent(state)?.venue;

    return {
        mainEvent: mainEvent ?? null,
        selectedEvent: selectedEvent ?? null,
        selectedVenueId: selectedVenue?.venue_id ? Number(selectedVenue.venue_id) : null,
        payments: state.payment.payments,
        confirmationNumber: state.reservation.confirmation_number,
        inLastStep: inLastStep(state),
        currency: state.reservation.currency || 'USD',
        orderTotal: getOrderTotal(state)?.amount ?? 0,
        tickets: state.tickets.tickets,
        productSales: state.productSales.products,
        bundleSales: state.bundleSales.bundles,
        hasInsurance: !!state.reservation.insuranceProvider,
        isSeries: isSeries(state),
        calendarOnListing: !!getSeriesOption("calendarOnListing", false)(state),
        priceLevels: state.priceLevel.levels,
        bundles: state.bundles.bundles,
        products: state.products.products,
        reservationCurrency: state.reservation.currency,
        overlayVisible: state.ui.overlay,
        ticketsDisplayType: state.ui.listingConfig?.listings?.ticketsDisplayType,
        calendarDisplayType: getSeriesConfig(state).calendarType,
        calendarOnListing: !!getSeriesOption("calendarOnListing", false)(state),
        listingSlug: state.ui.listingSlug,
        isSeries: isSeries(state),
        reservation: state.reservation,
        skipMembership: state.ui.skipMembership,
    }
}

const mapDispatchToProps = dispatch => {
    const actions = {};
    Object.keys(hookActions).forEach(hookActionName => {
        actions[hookActionName] = hookActions[hookActionName];
    });
    return bindActionCreators(actions, dispatch);
}

GoogleAnalytics4.propTypes = {
    config: PropTypes.object.isRequired,
    addReservationCompleteHook: PropTypes.func,
    addTicketsAddedToCartHook: PropTypes.func,
    addProductsAddedToCartHook: PropTypes.func,
    addBundlesAddedToCartHook: PropTypes.func,
    addTicketsRemovedFromCartHook: PropTypes.func,
    addProductsRemovedFromCartHook: PropTypes.func,
    addBundlesRemovedFromCartHook: PropTypes.func,
    addStateTransitionHook: PropTypes.func,
    addReservationClearHook: PropTypes.func,
    addReservationExpiresInHook: PropTypes.func,
    addCartTimerExtendedHook: PropTypes.func,
    currency: PropTypes.string,
    orderTotal: PropTypes.number,
    hasInsurance: PropTypes.bool,
}

export default connect(mapStateToProps, mapDispatchToProps)(GoogleAnalytics4)
